import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { audiencesLoadingSelector, audiencesSelector } from '../../../../../../modules/selectors/audiences'
import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import {
  clearUpdateDv360BulkTargeting,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import {
  CUSTOM_AUDIENCES,
  CUSTOM_AUDIENCES_EXCLUSION
} from '../../../../../ReusableFormFields/Audiences/AudiencesFields/fields'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { getInitialValues } from './fields'

function AudienceSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)

  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const audiences = useSelector(audiencesSelector)
  const audiencesLoading = useSelector(audiencesLoadingSelector)

  const formattedAudiences = useMemo(
    () =>
      audiences.map(({ id, name }) => ({
        value: id,
        label: name
      })),
    [audiences]
  )

  const initialValues = useMemo(() => {
    return getInitialValues(targeting, audiences)
  }, [targeting, audiences])

  const handleSubmit = useCallback(
    values => {
      const requests = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_AUDIENCE_GROUP,
          assigned_targeting_options: [
            {
              ...(!!values[CUSTOM_AUDIENCES].length && {
                included_first_and_third_party_audience_groups: [
                  {
                    settings: values[CUSTOM_AUDIENCES].map(item => ({
                      firstAndThirdPartyAudienceId: item.value
                    }))
                  }
                ]
              }),
              ...(!!values[CUSTOM_AUDIENCES_EXCLUSION].length && {
                excluded_first_and_third_party_audience_group: {
                  settings: values[CUSTOM_AUDIENCES_EXCLUSION].map(item => ({
                    firstAndThirdPartyAudienceId: item.value
                  }))
                }
              })
            }
          ]
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = purifiedFormik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <FieldLabelWrapper label={t('Custom audience inclusions')}>
        <MultiSelectBox
          placeholder="Add an audience inclusion"
          options={formattedAudiences}
          name={CUSTOM_AUDIENCES}
          value={values[CUSTOM_AUDIENCES]}
          isLoading={audiencesLoading}
          setFieldValue={setFieldValue}
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Custom audience exclusion')}>
        <MultiSelectBox
          placeholder={t('Exclude an audience')}
          options={formattedAudiences}
          value={values[CUSTOM_AUDIENCES_EXCLUSION]}
          name={CUSTOM_AUDIENCES_EXCLUSION}
          isLoading={audiencesLoading}
          setFieldValue={setFieldValue}
        />
      </FieldLabelWrapper>
    </EditForm>
  )
}

export default AudienceSectionForm
